import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Mail, MessageSquare, Trash2, Reply, X, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import AdminFilters from '../components/filters/AdminFilters'
import AdminPagination from '../components/pagination/AdminPagination'
import { useAdminFilters } from '../hooks/useAdminFilters'
import { useModalScrollLock } from '../hooks/useModalScrollLock'
import { api } from '../lib/api'

interface ContactMessage {
  id: string
  fullName: string
  email: string
  phone: string
  subject: string
  message: string
  status: 'NEW' | 'READ' | 'REPLIED' | 'CLOSED'
  reply?: string
  repliedAt?: string
  createdAt: string
}

const STATUS_COLORS = {
  NEW: 'bg-blue-100 text-blue-800 border border-blue-200',
  READ: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
  REPLIED: 'bg-green-100 text-green-800 border border-green-200',
  CLOSED: 'bg-gray-100 text-gray-800 border border-gray-200',
}

function formatDate(dateInput?: string | Date | null) {
  if (!dateInput) return 'N/A'
  const val = typeof dateInput === 'object' && (dateInput as any)?.createdAt ? (dateInput as any).createdAt : dateInput
  const d = new Date(val)
  if (isNaN(d.getTime())) return 'N/A'
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getMessageDate(msg?: any) {
  if (!msg) return undefined
  return msg.createdAt || msg.created_at || msg.timestamp || msg.date
}

function formatSubject(rawSubject?: string): string {
  if (!rawSubject || !rawSubject.trim()) return 'General Support Request'

  let s = rawSubject.trim()

  const enumMap: Record<string, string> = {
    PAYMENT_PENDING: 'Payment Pending',
    ORDER_CONFIRMED: 'Order Confirmed',
    OUT_FOR_DELIVERY: 'Out for Delivery',
    PAYMENT_FAILED: 'Payment Failed',
    DELIVERED: 'Delivered',
    CANCELLED: 'Cancelled',
    RETURN_REQUESTED: 'Return Requested',
    REFUNDED: 'Refunded',
    NEED_HELP_ORDER: 'Need Help With Order',
    ORDER_SUPPORT: 'Order Support',
  }

  Object.entries(enumMap).forEach(([key, val]) => {
    s = s.replace(new RegExp(key, 'g'), val)
  })

  // Replace remaining underscores with spaces
  s = s.replace(/_/g, ' ')

  // If subject contains an ID (e.g. Order Support clx123abc), format nicely as Order Support (#clx123abc)
  s = s.replace(/(Order\s+(?:Support|Help|Request)|Need\s+Help\s+With\s+Order)\s+([a-z0-9]{6,})/gi, '$1 (#$2)')

  // If text is ALL CAPS, convert to Capital Case
  if (s === s.toUpperCase()) {
    s = s.toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())
  }

  return s.replace(/\s+/g, ' ').trim()
}

export default function ContactMessagesPage() {
  const filterManager = useAdminFilters({ defaultLimit: 10 })
  const { search, debouncedSearch, page, limit, hasActiveFilters, setSearch, setPage, setLimit, setFilter, removeFilter, clearFilters } = filterManager

  const [messages, setMessages] = useState<ContactMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null)
  const [showReplyModal, setShowReplyModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')
  const [sendingReply, setSendingReply] = useState(false)
  const [deletingMessage, setDeletingMessage] = useState(false)

  // Prevent background page scrolling when modal is open
  useModalScrollLock(!!selectedMessage || showDeleteModal)

  useEffect(() => {
    fetchMessages()
  }, [page, limit, debouncedSearch, JSON.stringify(filterManager.filters)])

  const fetchMessages = async () => {
    try {
      setLoading(true)
      const response = await api.getContactMessages(page, limit, debouncedSearch, filterManager.filters)

      // Safely handle response - ensure data is always an array
      const data = response.data?.data || response.data || []
      const messagesArray = Array.isArray(data) ? data : []

      setMessages(messagesArray)
      setTotal(response.data?.pagination?.total || response.data?.total || 0)
    } catch (error) {
      toast.error('Failed to load contact messages')
      console.error(error)
      setMessages([])
    } finally {
      setLoading(false)
    }
  }

  const handleViewMessage = async (message: ContactMessage) => {
    try {
      const response = await api.getContactMessage(message.id)
      setSelectedMessage(response.data.data)
    } catch (error) {
      toast.error('Failed to load message details')
    }
  }

  const handleReplySubmit = async () => {
    if (!replyText.trim() || !selectedMessage) return

    try {
      setSendingReply(true)
      await api.replyToContactMessage(selectedMessage.id, replyText)
      toast.success('Reply sent successfully')
      setReplyText('')
      setShowReplyModal(false)
      setSelectedMessage(null)
      fetchMessages()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send reply')
    } finally {
      setSendingReply(false)
    }
  }

  const handleDeleteClick = (id: string) => {
    setDeleteTargetId(id)
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return

    try {
      setDeletingMessage(true)
      await api.deleteContactMessage(deleteTargetId)
      toast.success('Message deleted successfully')
      setShowDeleteModal(false)
      setDeleteTargetId(null)
      fetchMessages()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete message')
    } finally {
      setDeletingMessage(false)
    }
  }

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await api.updateContactMessageStatus(id, newStatus)
      toast.success('Status updated')
      fetchMessages()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update status')
    }
  }

  if (loading && messages.length === 0) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D3A0D]"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-2 sm:px-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#6D3A0D]">Contact Messages</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Manage inquiries and support requests</p>
        </div>
        <span className="text-xs sm:text-sm font-semibold text-gray-600 bg-gray-100 px-3 py-1.5 rounded-lg self-start sm:self-auto">
          Total: {total} messages
        </span>
      </div>

      <AdminFilters
        search={search}
        searchPlaceholder="Search messages by sender, email, or subject..."
        onSearchChange={setSearch}
        filters={filterManager.filters}
        onFilterChange={setFilter}
        onRemoveFilter={removeFilter}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
        isLoading={loading}
        fields={[
          {
            key: 'status',
            label: 'Message Status',
            type: 'select',
            options: [
              { label: 'New', value: 'NEW' },
              { label: 'Read', value: 'READ' },
              { label: 'Replied', value: 'REPLIED' },
              { label: 'Closed', value: 'CLOSED' },
            ],
          },
        ]}
      />

      {loading ? (
        <div className="text-center py-12 bg-white rounded-2xl border-2 border-[#F6EEE7]">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[#6D3A0D]"></div>
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border-2 border-[#F6EEE7] p-6">
          <p className="text-gray-600 font-medium">
            {hasActiveFilters ? 'No contact messages match your active filters.' : 'No contact messages yet.'}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="mt-3 inline-flex items-center text-xs font-semibold text-[#6D3A0D] underline hover:text-[#5D2A0D]"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className="bg-white border-2 border-[#F6EEE7] rounded-2xl p-4 sm:p-6 hover:shadow-sm transition"
            >
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="font-bold text-[#6D3A0D] text-base truncate">{message.fullName || 'Anonymous'}</h3>
                    <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full ${STATUS_COLORS[message.status]}`}>
                      {message.status}
                    </span>
                  </div>

                  <div className="space-y-1 text-xs sm:text-sm text-gray-600 mb-3">
                    <p className="truncate">
                      <span className="font-semibold text-gray-700">Email:</span> {message.email}
                    </p>
                    {message.phone && (
                      <p className="truncate">
                        <span className="font-semibold text-gray-700">Phone:</span> {message.phone}
                      </p>
                    )}
                    <p className="font-medium text-gray-800 pt-1">
                      <span className="font-semibold text-gray-700">Subject:</span> {formatSubject(message.subject)}
                    </p>
                  </div>

                  <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mb-3 bg-[#F6EEE7]/30 p-2.5 rounded-lg border border-[#F6EEE7]">
                    {message.message}
                  </p>

                  <p className="text-xs font-medium text-gray-500">
                    Received: {formatDate(getMessageDate(message))}
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0 self-end sm:self-start pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-100 w-full sm:w-auto justify-end">
                  <button
                    onClick={() => {
                      handleViewMessage(message)
                      setShowReplyModal(false)
                    }}
                    className="p-2 text-[#6D3A0D] hover:bg-[#F6EEE7] rounded-xl transition flex items-center gap-1 text-xs font-semibold border border-[#F6EEE7]"
                    title="View Details"
                  >
                    <MessageSquare size={16} />
                    <span className="sm:hidden">View</span>
                  </button>

                  <button
                    onClick={() => {
                      handleViewMessage(message)
                      setShowReplyModal(true)
                    }}
                    className="p-2 text-[#6D3A0D] hover:bg-[#F6EEE7] rounded-xl transition flex items-center gap-1 text-xs font-semibold border border-[#F6EEE7]"
                    title="Reply"
                  >
                    <Reply size={16} />
                    <span className="sm:hidden">Reply</span>
                  </button>

                  <button
                    onClick={() => handleDeleteClick(message.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition flex items-center gap-1 text-xs font-semibold border border-red-100"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                    <span className="sm:hidden">Delete</span>
                  </button>
                </div>
              </div>

              {/* Status Dropdown */}
              <div className="mt-4 pt-3 border-t border-[#F6EEE7] flex items-center justify-between">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Change Status:</span>
                <select
                  value={message.status}
                  onChange={(e) => handleStatusChange(message.id, e.target.value)}
                  className="text-xs font-semibold px-3 py-1.5 border-2 border-[#F6EEE7] rounded-xl focus:outline-none focus:border-[#6D3A0D] bg-white text-gray-700"
                >
                  <option value="NEW">New</option>
                  <option value="READ">Read</option>
                  <option value="REPLIED">Replied</option>
                  <option value="CLOSED">Closed</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div className="mt-6">
          <AdminPagination
            page={page}
            limit={limit}
            total={total}
            onPageChange={setPage}
            onLimitChange={setLimit}
            isLoading={loading}
            showInfo={true}
          />
        </div>
      )}

      {/* Message Detail & Reply Modal */}
      {selectedMessage && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-[9999] p-3 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl border-2 border-[#F6EEE7] max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-[#F6EEE7]">
              <h2 className="text-xl sm:text-2xl font-bold text-[#6D3A0D]">Message Details</h2>
              <button
                onClick={() => {
                  setSelectedMessage(null)
                  setShowReplyModal(false)
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6 space-y-4 text-sm">
              <div>
                <h3 className="font-bold text-[#6D3A0D] uppercase text-xs tracking-wider mb-1">From</h3>
                <p className="text-gray-900 font-semibold">{selectedMessage.fullName}</p>
                <p className="text-gray-600 text-xs">{selectedMessage.email}</p>
                {selectedMessage.phone && (
                  <p className="text-gray-600 text-xs">{selectedMessage.phone}</p>
                )}
              </div>

              <div>
                <h3 className="font-bold text-[#6D3A0D] uppercase text-xs tracking-wider mb-1">Subject</h3>
                <p className="text-gray-800 font-medium">{formatSubject(selectedMessage.subject)}</p>
              </div>

              <div>
                <h3 className="font-bold text-[#6D3A0D] uppercase text-xs tracking-wider mb-1">Message</h3>
                <p className="text-gray-700 bg-gray-50 p-3 rounded-xl border border-gray-200 whitespace-pre-wrap leading-relaxed">
                  {selectedMessage.message}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Sent on: {formatDate(getMessageDate(selectedMessage))}
                </p>
              </div>

              {selectedMessage.reply && (
                <div className="bg-[#F6EEE7]/60 p-4 rounded-xl border border-[#F6EEE7]">
                  <h3 className="font-bold text-[#6D3A0D] uppercase text-xs tracking-wider mb-2">Your Reply</h3>
                  <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">{selectedMessage.reply}</p>
                  {(selectedMessage.repliedAt || (selectedMessage as any).replied_at) && (
                    <p className="text-xs font-medium text-[#6D3A0D] mt-2">
                      Replied on: {formatDate(selectedMessage.repliedAt || (selectedMessage as any).replied_at)}
                    </p>
                  )}
                </div>
              )}

              {/* Reply Form */}
              {showReplyModal && !selectedMessage.reply && (
                <div className="space-y-3 pt-2 border-t border-[#F6EEE7]">
                  <label className="block">
                    <span className="text-xs font-bold text-[#6D3A0D] uppercase tracking-wider mb-1 block">
                      Your Reply <span className="text-red-500">*</span>
                    </span>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Write your reply here..."
                      rows={4}
                      className="w-full px-3.5 py-2.5 border-2 border-[#6D3A0D] rounded-xl text-sm focus:outline-none"
                    />
                  </label>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowReplyModal(false)}
                      className="flex-1 px-4 py-2.5 border-2 border-[#6D3A0D] text-[#6D3A0D] rounded-xl font-bold hover:bg-[#F6EEE7] transition text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleReplySubmit}
                      disabled={!replyText.trim() || sendingReply}
                      className="flex-1 px-4 py-2.5 bg-[#6D3A0D] text-white rounded-xl font-bold hover:bg-[#5D2A0D] transition disabled:opacity-50 text-sm shadow-sm flex items-center justify-center gap-2"
                    >
                      {sendingReply ? 'Sending...' : 'Send Reply'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-[9999] p-3 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl border-2 border-[#F6EEE7] max-w-sm w-full shadow-xl">
            <div className="flex items-center justify-center p-5 bg-red-50 border-b border-[#F6EEE7] rounded-t-2xl">
              <AlertCircle size={28} className="text-red-600 mr-2.5" />
              <h2 className="text-lg font-bold text-red-600">Delete Message</h2>
            </div>

            <div className="p-5">
              <p className="text-gray-600 text-sm mb-5">
                Are you sure you want to delete this message? This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deletingMessage}
                  className="flex-1 px-4 py-2.5 border-2 border-[#6D3A0D] text-[#6D3A0D] rounded-xl font-bold hover:bg-[#F6EEE7] transition text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={deletingMessage}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deletingMessage ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
